import { StripeTerminalPluginNotFoundError } from '../../store/StripeTerminal/StripeTerminalPluginNotFoundError';
import type {
  CordovaTapToPayCollectPaymentSuccessPayload,
  CordovaTapToPayConnectionStatusPayload,
  CordovaTapToPayDetectDeviceSuccessPayload,
  CordovaTapToPayErrorPayload,
  CordovaTapToPayInitSuccessPayload,
} from '../CordovaPlugins.types';
import { TapToPayConnectionStatus, TapToPayGeolocationAuthorizationStatus } from './TapToPayPlugin.types';

/**
 * Note: If a call to a plugin results includes listening in on different events through the
 * onSuccess callback that's been kept alive on the Swift side, remember to include
 * onSuccess/onFailure arguments in the corresponding method's arguments in TapToPayPlugin.
 * See the init and detectDevices methods for examples.
 */
export class TapToPayPlugin {
  private readonly fetchToken: () => Promise<string>;
  private readonly locationId: string;
  private readonly merchantName: string;
  private readonly onBehalfOfId: string;

  constructor({
    fetchToken, locationId, merchantName, onBehalfOfId,
  }: {
    fetchToken: () => Promise<string>,
    locationId: string,
    merchantName: string,
    onBehalfOfId: string,
  }) {
    this.fetchToken = fetchToken;
    this.locationId = locationId;
    this.merchantName = merchantName;
    this.onBehalfOfId = onBehalfOfId;
  }

  static init(
    onSuccess: (payload: CordovaTapToPayInitSuccessPayload) => Promise<void> | void = () => {},
    onFailure: (payload?: CordovaTapToPayErrorPayload) => Promise<void> | void = () => {},
  ): void {
    if (!window?.cordovaTapToPay) {
      onFailure();
      return;
    }
    window.cordovaTapToPay.init(onSuccess, onFailure);
  }

  private static async configConnectionToken(
    connectionToken: string,
  ) {
    return new Promise<void>(
      (resolve, reject) => {
        if (!window?.cordovaTapToPay) {
          throw new StripeTerminalPluginNotFoundError('cordovaTapToPay not loaded');
        }

        return window.cordovaTapToPay.configConnectionToken(
          connectionToken,
          () => { resolve(); },
          reject,
        );
      },
    );
  }

  private async detectDevices(
    onSuccess: (payload: CordovaTapToPayDetectDeviceSuccessPayload) => void | PromiseLike<void>,
    onFailure?: (payload?: CordovaTapToPayErrorPayload) => void | PromiseLike<void>,
  ): Promise<CordovaTapToPayDetectDeviceSuccessPayload> {
    return new Promise<CordovaTapToPayDetectDeviceSuccessPayload>(
      (resolve, reject) => {
        if (!window?.cordovaTapToPay) {
          throw new StripeTerminalPluginNotFoundError('cordovaTapToPay not loaded');
        }

        return window.cordovaTapToPay.detectDevices(
          this.locationId,
          this.merchantName,
          this.onBehalfOfId,
          (payload: CordovaTapToPayDetectDeviceSuccessPayload) => {
            onSuccess(payload);
            resolve(payload);
          },
          (payload: CordovaTapToPayErrorPayload = { name: 'unknown error', description: '' }) => {
            onFailure?.(payload);
            reject(payload);
          },
        );
      },
    );
  }

  static async connectionStatus(): Promise<TapToPayConnectionStatus> {
    return new Promise<TapToPayConnectionStatus>(
      resolve => {
        if (!window?.cordovaTapToPay) {
          throw new StripeTerminalPluginNotFoundError('cordovaTapToPay not loaded');
        }

        window.cordovaTapToPay.connectionStatus(
          ({ payload }: CordovaTapToPayConnectionStatusPayload) => {
            resolve(Number(payload));
          },
        );
      },
    );
  }

  connectToReader(
    onSuccess: (payload: CordovaTapToPayDetectDeviceSuccessPayload) => void | PromiseLike<void>,
    onFailure?: (payload?: CordovaTapToPayErrorPayload) => void | PromiseLike<void>,
  ): void {
    this.fetchToken()
      .then((token: string) => TapToPayPlugin.configConnectionToken(token))
      .then(() => {
        this.detectDevices(onSuccess, onFailure);
      });
  }

  static async disconnectReader(
    force: boolean = false,
  ): Promise<void> {
    return new Promise<void>(
      (resolve, reject) => {
        if (!window?.cordovaTapToPay) {
          throw new StripeTerminalPluginNotFoundError('cordovaTapToPay not loaded');
        }

        window.cordovaTapToPay.disconnectReader(force, resolve, reject);
      },
    );
  }

  static async collectPayment(
    clientSecret: string,
    onSuccess: (payload: CordovaTapToPayCollectPaymentSuccessPayload) => void | PromiseLike<void>,
    onFailure?: (payload?: CordovaTapToPayErrorPayload) => void | PromiseLike<void>,
  ) {
    return new Promise<CordovaTapToPayCollectPaymentSuccessPayload>((resolve, reject) => {
      if (!window?.cordovaTapToPay) {
        throw new StripeTerminalPluginNotFoundError('cordovaTapToPay not loaded');
      }

      window.cordovaTapToPay.collectPayment(
        clientSecret,
        (payload: CordovaTapToPayCollectPaymentSuccessPayload) => {
          onSuccess(payload);
          resolve(payload);
        },
        (payload: CordovaTapToPayErrorPayload = { name: 'unknown error', description: '' }) => {
          onFailure?.(payload);
          reject(payload);
        },
      );
    });
  }

  static async geolocationAuthorizationStatus(): Promise<TapToPayGeolocationAuthorizationStatus> {
    return new Promise<TapToPayGeolocationAuthorizationStatus>((resolve, reject) => {
      if (!window?.cordovaTapToPay) {
        throw new StripeTerminalPluginNotFoundError('cordovaTapToPay not loaded');
      }

      window.cordovaTapToPay.locationAuthorizationStatus(
        (payload: TapToPayGeolocationAuthorizationStatus) => {
          resolve(payload);
        },
        (payload?: CordovaTapToPayErrorPayload) => {
          reject(payload ?? 'geolocationAuthorizationStatus: unknown error');
        },
      );
    });
  }

  static async openSettingsApp() {
    return new Promise<void>((resolve, reject) => {
      if (!window?.cordovaTapToPay) {
        throw new StripeTerminalPluginNotFoundError('cordovaTapToPay not loaded');
      }

      window.cordovaTapToPay.openSettingsApp(
        () => {
          resolve();
        },
        (payload?: CordovaTapToPayErrorPayload) => {
          reject(payload ?? 'openSettingsApp: unknown error');
        },
      );
    });
  }
}
